import React from 'react';
import { Loader2, ChevronRight } from 'lucide-react';
import { getAssetIcon } from './VrMindMapUtils';

const AssetListItem = React.memo(({ asset, type, isSelected, onSelect }) => {
  return (
    <button
      onClick={() => onSelect({ ...asset, type })}
      className={`w-full flex items-center gap-3 p-3 rounded-lg transition ${
        isSelected
          ? 'bg-[#2B2C2F] text-white border border-[#37383F]'
          : 'bg-[#1A1B1E] text-gray-300 hover:bg-[#2B2C2F] border border-transparent'
      }`}
    >
      {getAssetIcon(type)}
      <div className="flex-1 text-left">
        <div className="font-medium truncate">
          {asset.file_name || asset.asset_file_name}
        </div>
        <div
          className={`text-xs ${isSelected ? 'text-gray-400' : 'text-gray-500'}`}
        >
          {type}
        </div>
      </div>
    </button>
  );
});

AssetListItem.displayName = 'AssetListItem';

const AssetSidebar = ({
  assets,
  loading,
  selectedAsset,
  onAssetSelect,
  collapsed,
  onToggleCollapse,
}) => {
  return (
    <>
      <div
        className={`${collapsed ? 'w-0' : 'w-80'} transition-all duration-300 bg-[#1A1B1E] shadow-lg overflow-hidden flex flex-col border-r border-[#37383F]`}
      >
        <div className="p-4 border-b border-[#37383F]">
          <h2 className="text-xl font-bold text-white">Assets</h2>
          <p className="text-sm text-gray-400">
            Select an asset to view VR properties
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#EE2B2A]" />
            </div>
          ) : (
            <>
              {assets.mesh.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">
                    Mesh Assets
                  </h3>
                  <div className="space-y-2">
                    {assets.mesh.map((asset) => (
                      <AssetListItem
                        key={asset.id || asset._id}
                        asset={asset}
                        type="Mesh"
                        isSelected={
                          selectedAsset?.id === asset.id ||
                          selectedAsset?._id === asset._id
                        }
                        onSelect={onAssetSelect}
                      />
                    ))}
                  </div>
                </div>
              )}

              {assets.video.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">
                    Video Assets
                  </h3>
                  <div className="space-y-2">
                    {assets.video.map((asset) => (
                      <AssetListItem
                        key={asset.id || asset._id}
                        asset={asset}
                        type="Video"
                        isSelected={
                          selectedAsset?.id === asset.id ||
                          selectedAsset?._id === asset._id
                        }
                        onSelect={onAssetSelect}
                      />
                    ))}
                  </div>
                </div>
              )}

              {assets.audio.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">
                    Audio Assets
                  </h3>
                  <div className="space-y-2">
                    {assets.audio.map((asset) => (
                      <AssetListItem
                        key={asset.id || asset._id}
                        asset={asset}
                        type="Audio"
                        isSelected={
                          selectedAsset?.id === asset.id ||
                          selectedAsset?._id === asset._id
                        }
                        onSelect={onAssetSelect}
                      />
                    ))}
                  </div>
                </div>
              )}

              {assets.image.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">
                    Image Assets
                  </h3>
                  <div className="space-y-2">
                    {assets.image.map((asset) => (
                      <AssetListItem
                        key={asset.id || asset._id}
                        asset={asset}
                        type="Image"
                        isSelected={
                          selectedAsset?.id === asset.id ||
                          selectedAsset?._id === asset._id
                        }
                        onSelect={onAssetSelect}
                      />
                    ))}
                  </div>
                </div>
              )}

              {assets.text.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">
                    Text Assets
                  </h3>
                  <div className="space-y-2">
                    {assets.text.map((asset) => (
                      <AssetListItem
                        key={asset.id || asset._id}
                        asset={asset}
                        type="Text"
                        isSelected={
                          selectedAsset?.id === asset.id ||
                          selectedAsset?._id === asset._id
                        }
                        onSelect={onAssetSelect}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <button
        onClick={onToggleCollapse}
        className="absolute left-0 top-1/2 -translate-y-1/2 bg-[#1A1B1E] border border-[#37383F] shadow-lg rounded-r-lg p-2 hover:bg-[#2B2C2F] transition z-10 text-white"
        style={{ left: collapsed ? '0' : '320px' }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ChevronRight
          className={`w-5 h-5 transition-transform ${collapsed ? '' : 'rotate-180'}`}
        />
      </button>
    </>
  );
};

export default AssetSidebar;
